import type { NitroFetchOptions } from "nitropack";
import type { UseApiFetchOptions } from "@edco/apollo/dist/runtime/composables/useApiFetch";
import type { AsyncData } from "#app";
import type { NferPrice, NferProduct } from "~/types/product";

export type NferOrderLine = {
  id?: string;
  orderId: string;
  product: NferProduct;
  price: NferPrice;
  quantity: number;
  priceExcVat: number;
  priceIncVat: number;
  itemPrice: number;
  discountValue: number;
  discountPercentage: number;
  isCarriage: boolean;
};

export type NferOrder = {
  id?: string;
  orderLines: NferOrderLine[];
  totalExcVat: number;
  totalIncVat: number;
  totalVat: number;
  totalQuantity: number;
  currency?: string;
  invoiceAddressId?: string;
  invoiceOrgId?: string;
  deliveryAddressId?: string;
  deliveryOrgId?: string;
  carriageId?: number;
  totalDiscount: number;
  totalShipping: number;
  subtotalBeforeDiscount: number;
  vouchers?: Record<string, string>;
  carriageRequired: boolean;
};

export type AddToBasket = {
  productId: number;
  quantity: number;
  priceId: number;
};

export type NferOrderLineAddResult = {
  productId: number;
  productIdGuid?: string;
  pricingScheduleId?: number;
  productFormatId?: number;
  productLicenceTypeId?: number;
  orderLineId?: string;
  errorCode: number;
  errorMessage: string;
};

export type ApplyVoucherResult = {
  discountValue?: number;
  userMessage?: string;
  errorCode?: number;
  errorMessage?: string;
  success: boolean;
};

export type NferOrderSaveResult = {
  removeResults: Record<string, boolean>;
  addResults: NferOrderLineAddResult[];
  sopAddResult: {
    id: string;
    reference: string;
  };
};

export type NferOrderValidation = {
  isValid: boolean;
  vouchersValid: boolean;
  invalidVouchers: Record<string, string>;
  preRequesiteProductsMet: boolean;
  unmetPreRequesiteProducts: string[];
  purchaseAllowed: boolean;
  disallowedOrderLines: Record<string, string>;
  hasCarriageApplied: boolean;
  requiredCarriage: boolean;
};

export function $nferBasketResource () {
  const dependencies = useFetchDependencies();

  function getWarnings ( abortSignal?: AbortSignal ) {
    const url = "/api/nfer/basket/warnings";

    const options: NitroFetchOptions<string> = {
      method: "GET",
      signal: abortSignal,
      headers: {
        Accept: "application/json",
      },
    };

    return $apiFetch<string[] | undefined>( url, options, dependencies );
  }

  function applyCarriage ( abortSignal?: AbortSignal ) {
    const url = "/api/nfer/basket/carriage";

    const options: NitroFetchOptions<string> = {
      method: "POST",
      signal: abortSignal,
      headers: {
        Accept: "application/json",
      },
    };

    return $apiFetch<boolean | undefined>( url, options, dependencies );
  }

  function applyMultiShipCarriage ( abortSignal?: AbortSignal ) {
    const url = "/api/nfer/basket/carriage/multiship";

    const options: NitroFetchOptions<string> = {
      method: "POST",
      signal: abortSignal,
      headers: {
        Accept: "application/json",
      },
    };

    return $apiFetch<boolean | undefined>( url, options, dependencies );
  }

  function getBasket ( abortSignal?: AbortSignal ) {
    const url = "/api/nfer/basket";

    const options: NitroFetchOptions<string> = {
      method: "GET",
      signal: abortSignal,
      headers: {
        Accept: "application/json",
      },
    };

    return $apiFetch<NferOrder>( url, options, dependencies );
  }

  function addToBasket ( input: AddToBasket | AddToBasket[] ) {
    const url = "/api/nfer/basket/lines";

    const body = Array.isArray( input ) ? input : [input];

    const options: NitroFetchOptions<string> = {
      method: "POST",
      headers: {
        Accept: "application/json",
      },
      body,
    };

    return $apiFetch<NferOrderSaveResult>( url, options, dependencies );
  }

  function removeFromBasket ( lineId: string ) {
    let url = "/api/nfer/basket/lines/remove?";

    if ( lineId !== undefined && lineId !== null ) {
      url += `lineId=${encodeURIComponent( lineId )}`;
    }

    url = url.replace( /[?&]$/, "" );

    const options: NitroFetchOptions<string> = {
      method: "POST",
      headers: {
        Accept: "application/json",
      },
    };

    return $apiFetch<NferOrderSaveResult>( url, options, dependencies );
  }

  function updateQuantity ( lineId: string, newQuantity: number ) {
    let url = "/api/nfer/basket/lines/quantity?";

    if ( lineId !== undefined && lineId !== null ) {
      url += `lineId=${encodeURIComponent( lineId )}&`;
    }

    if ( newQuantity !== undefined && newQuantity !== null ) {
      url += `newQuantity=${encodeURIComponent( newQuantity )}`;
    }

    url = url.replace( /[?&]$/, "" );

    const options: NitroFetchOptions<string> = {
      method: "POST",
      headers: {
        Accept: "application/json",
      },
    };

    return $apiFetch<NferOrderSaveResult>( url, options, dependencies );
  }

  function clearBasket () {
    const url = "/api/nfer/basket/clear";

    const options: NitroFetchOptions<string> = {
      method: "POST",
      headers: {
        Accept: "application/json",
      },
    };

    return $apiFetch<boolean>( url, options, dependencies );
  }

  function applyVoucher ( voucher: string, removeExistingVouchers: boolean ) {
    let url = "/api/nfer/basket/voucher?";

    if ( voucher !== undefined && voucher !== null ) {
      url += `voucher=${encodeURIComponent( voucher )}&`;
    }

    if ( removeExistingVouchers !== undefined && removeExistingVouchers !== null ) {
      url += `removeExistingVouchers=${encodeURIComponent( removeExistingVouchers )}&`;
    }

    url = url.replace( /[?&]$/, "" );

    const options: NitroFetchOptions<string> = {
      method: "POST",
      headers: {
        Accept: "application/json",
      },
    };

    return $apiFetch<ApplyVoucherResult>( url, options, dependencies );
  }

  function removeVoucher ( voucher: string ) {
    let url = "/api/nfer/basket/voucher/remove?";

    if ( voucher !== undefined && voucher !== null ) {
      url += `voucher=${encodeURIComponent( voucher )}&`;
    }

    url = url.replace( /[?&]$/, "" );

    const options: NitroFetchOptions<string> = {
      method: "POST",
      headers: {
        Accept: "application/json",
      },
    };

    return $apiFetch<boolean>( url, options, dependencies );
  }

  function setAddress ( addressId: string, type: "delivery" | "invoice", orgId?: string ) {
    let url = "/api/nfer/basket/address?";

    if ( addressId !== undefined && addressId !== null ) {
      url += `addressId=${encodeURIComponent( addressId )}&`;
    }

    if ( orgId !== undefined && orgId !== null ) {
      url += `orgId=${encodeURIComponent( orgId )}&`;
    }

    if ( type !== undefined && type !== null ) {
      url += `type=${encodeURIComponent( type )}&`;
    }

    url = url.replace( /[?&]$/, "" );

    const options: NitroFetchOptions<string> = {
      method: "POST",
      headers: {
        Accept: "application/json",
      },
    };

    return $apiFetch<NferOrderSaveResult>( url, options, dependencies );
  }

  function validate ( abortSignal?: AbortSignal ) {
    const url = "/api/nfer/basket/validate";

    const options: NitroFetchOptions<string> = {
      method: "GET",
      signal: abortSignal,
      headers: {
        Accept: "application/json",
      },
    };

    return $apiFetch<NferOrderValidation | null>( url, options, dependencies );
  }

  return {
    getBasket,
    addToBasket,
    removeFromBasket,
    updateQuantity,
    clearBasket,
    applyVoucher,
    removeVoucher,
    setAddress,
    validate,
    getWarnings,
    applyCarriage,
    applyMultiShipCarriage,
  };
}

export function useNferBasketResource () {
  function getWarnings (): AsyncData<string[] | undefined, Error> {
    const url = "/api/nfer/basket/warnings";

    const options: UseApiFetchOptions<string[] | null> = {
      method: "GET",
      headers: {
        Accept: "application/json",
      },
    };

    return useApiFetch<string[] | undefined>( url, options );
  }

  function getBasket (): AsyncData<NferOrder, Error> {
    const url = "/api/nfer/basket";

    const options: UseApiFetchOptions<NferOrder> = {
      method: "GET",
      headers: {
        Accept: "application/json",
      },
    };

    return useApiFetch<NferOrder>( url, options );
  }

  function getBasketCurrency (): AsyncData<string | null, Error> {
    const url = "/api/nfer/basket/currency";

    const options: UseApiFetchOptions<string | null> = {
      method: "GET",
      headers: {
        Accept: "application/json",
      },
    };

    return useApiFetch<string | null>( url, options );
  }

  function validate () {
    const url = "/api/nfer/basket/validate";

    const options: UseApiFetchOptions<NferOrderValidation | null> = {
      method: "GET",
      headers: {
        Accept: "application/json",
      },
    };

    return useApiFetch<NferOrderValidation | null>( url, options );
  }

  return {
    getBasket,
    getBasketCurrency,
    getWarnings,
    validate,
  };
}
